<template>
  <v-col>
    <!-- Compact Header Section with Inline Tabs -->
    <div class="clients-view-header-compact mb-3">
      <v-toolbar
        flat
        color="white"
        class="header-toolbar px-0 py-0"
        height="56"
      >
        <!-- Title Icon -->
        <div class="header-icon-wrapper mr-3">
          <font-awesome-icon
            :icon="['fas', 'building']"
            size="lg"
            class="primary--text"
          />
        </div>

        <!-- Inline Tabs -->
        <v-tabs
          v-model="activeTab"
          color="primary"
          slider-color="primary"
          show-arrows
          class="clients-view-tabs-inline flex-grow-1"
          height="56"
        >
          <v-tab
            v-for="tab in tabs"
            :key="tab.id"
            class="tab-compact"
          >
            <font-awesome-icon
              :icon="tab.icon"
              class="mr-2"
              size="sm"
            />
            {{ tab.label }}
            <v-chip
              v-if="tab.count !== undefined"
              x-small
              outlined
              :color="tab.chipColor || 'brightblue'"
              class="ml-2 tab-count-chip"
            >
              {{ tab.count }}
            </v-chip>
          </v-tab>
        </v-tabs>

        <v-spacer />

        <!-- Action Buttons -->
        <div class="header-actions d-flex align-center">
          <!-- Add Client Button (Admin Only) -->
          <v-btn
            v-if="isUserInternalAdmin"
            color="primary"
            depressed
            class="add-client-btn-compact"
            @click="addClient"
          >
            <font-awesome-icon
              :icon="['fas', 'plus']"
              size="sm"
            />
            <span class="ml-2 d-none d-sm-inline">Add Client</span>
          </v-btn>
        </div>
      </v-toolbar>

      <v-divider />

      <!-- Compact Controls Bar -->
      <div class="controls-bar">
        <v-row class="align-center ma-0 pa-3 pl-2">
          <!-- Search Field -->
          <v-col
            cols="12"
            sm="6"
            md="5"
            lg="4"
            class="py-1"
          >
            <v-text-field
              v-model="searchText"
              placeholder="Search by client name or billing code..."
              single-line
              hide-details
              dense
              outlined
              clearable
              class="search-field-compact"
            >
              <template v-slot:prepend-inner>
                <font-awesome-icon
                  :icon="['fas', 'magnifying-glass']"
                  size="sm"
                  class="search-icon mt-1"
                />
              </template>
            </v-text-field>
          </v-col>

          <!-- Filter Button -->
          <v-col
            cols="auto"
            class="py-1 d-flex align-center"
          >
            <v-btn
              text
              outlined
              color="primary"
              :class="{ 'filter-active': hasActiveFilters }"
              class="filter-btn-compact"
              @click="filterMenuOpen = !filterMenuOpen"
            >
              <font-awesome-icon
                :icon="['fas', 'filter']"
                size="sm"
                class="mr-1"
              />
              <span>Filters</span>
              <v-chip
                v-if="hasActiveFilters"
                x-small
                color="primary"
                class="ml-2"
              >
                {{ activeFilterCount }}
              </v-chip>
            </v-btn>
          </v-col>

          <!-- View Mode Toggle -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <v-btn-toggle
              v-model="viewMode"
              mandatory
              dense
              borderless
              color="primary"
            >
              <v-btn
                value="grid"
                small
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <font-awesome-icon
                      :icon="['fas', 'grip']"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <span>Grid View</span>
                </v-tooltip>
              </v-btn>
              <v-btn
                value="table"
                small
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <font-awesome-icon
                      :icon="['fas', 'table']"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <span>Table View</span>
                </v-tooltip>
              </v-btn>
            </v-btn-toggle>
          </v-col>

          <v-spacer class="d-none d-sm-flex" />
        </v-row>
      </div>
    </div>

    <!-- Filter Dialog -->
    <v-dialog
      v-model="filterMenuOpen"
      max-width="600"
      scrollable
    >
      <v-card>
        <v-card-title class="text-h6 primary white--text">
          <font-awesome-icon
            :icon="['fas', 'filter']"
            class="mr-2"
          />
          Filter Clients
          <v-spacer />
          <v-btn
            icon
            dark
            @click="filterMenuOpen = false"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-4">
          <!-- Basic Filters Section -->
          <div class="filter-section mb-4">
            <h3 class="text-subtitle-1 font-weight-bold mb-3">
              Basic Filters
            </h3>

            <v-select
              v-model="selectedStatusFilter"
              :items="statusFilterOptions"
              label="Status"
              outlined
              dense
              clearable
              hide-details
              class="mb-3"
              prepend-inner-icon="mdi-toggle-switch"
            />

            <v-select
              v-model="selectedTimesheetFilter"
              :items="timesheetFilterOptions"
              label="Timesheet Enabled"
              outlined
              dense
              clearable
              hide-details
              class="mb-3"
              prepend-inner-icon="mdi-clock-outline"
            />
          </div>

          <v-divider class="my-4" />

          <!-- Advanced Filters Section -->
          <div class="filter-section">
            <v-expansion-panels
              v-model="advancedFiltersOpen"
              flat
            >
              <v-expansion-panel>
                <v-expansion-panel-header class="px-0">
                  <div class="d-flex align-items-center">
                    <font-awesome-icon
                      :icon="['fas', 'sliders']"
                      class="mr-2"
                    />
                    <span class="font-weight-bold">Advanced Filters</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="px-0">
                  <v-select
                    v-model="selectedConfigFilter"
                    :items="configFilterOptions"
                    label="Configuration"
                    outlined
                    dense
                    clearable
                    hide-details
                    class="mb-3"
                    prepend-inner-icon="mdi-cog"
                  />

                  <v-select
                    v-model="selectedPublisherFilter"
                    :items="publisherFilterOptions"
                    label="Publisher Status"
                    outlined
                    dense
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-domain"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="px-4 py-3">
          <v-chip
            v-if="activeFilterCount > 0"
            small
            color="primary"
            text-color="white"
          >
            {{ activeFilterCount }} filter{{ activeFilterCount > 1 ? 's' : '' }} active
          </v-chip>
          <v-spacer />
          <v-btn
            text
            color="primary"
            @click="clearAllFilters"
          >
            Clear All
          </v-btn>
          <v-btn
            color="primary"
            depressed
            @click="filterMenuOpen = false"
          >
            Apply Filters
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading State -->
    <v-row
      v-if="loading && clients.length === 0"
      class="mt-4"
    >
      <v-col class="text-center py-12">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        />
        <p class="mt-4 text-subtitle-1">
          Loading clients...
        </p>
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-row
      v-else-if="error"
      class="mt-4"
    >
      <v-col>
        <v-alert
          type="error"
          prominent
        >
          <v-row align="center">
            <v-col class="grow">
              {{ error }}
            </v-col>
            <v-col class="shrink">
              <v-btn
                @click="fetchClientsData"
              >
                Retry
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Empty State -->
    <v-row
      v-else-if="filteredClients.length === 0"
      class="mt-4"
    >
      <v-col>
        <v-card
          class="pa-8 text-center"
          elevation="2"
        >
          <font-awesome-icon
            :icon="['fas', 'inbox']"
            size="4x"
            class="mb-4"
            style="color: #9e9e9e;"
          />
          <h3 class="text-h6 mb-2">
            No clients found
          </h3>
          <p class="text-body-2 text--secondary">
            {{ hasActiveFilters || searchText ? 'Try adjusting your filters or search terms' : 'No clients available in this view' }}
          </p>
          <v-btn
            v-if="hasActiveFilters || searchText"
            color="primary"
            outlined
            class="mt-4"
            @click="clearAllFilters"
          >
            Clear Filters
          </v-btn>
        </v-card>
      </v-col>
    </v-row>

    <!-- Client Cards Grid View -->
    <v-row
      v-else-if="viewMode === 'grid'"
      class="mt-4"
    >
      <v-col
        v-for="client in filteredClients"
        :key="client.billingCode"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <client-card
          :client="client"
          :config="configs[client.billingCode]"
          :publisher="publishers[client.billingCode]"
          :loading-config="isLoadingConfig(client.billingCode)"
          :loading-publisher="isLoadingPublisher(client.billingCode)"
          :is-admin="isUserInternalAdmin"
          @view-profile="navigateToProfile"
          @edit="editClient"
          @delete="deleteClient"
        />
      </v-col>
    </v-row>

    <!-- Clients Table View -->
    <v-row
      v-else-if="viewMode === 'table'"
      class="mt-4"
    >
      <v-col>
        <v-card elevation="2">
          <v-data-table
            :headers="tableHeaders"
            :items="filteredClients"
            :items-per-page="25"
            :footer-props="{
              'items-per-page-options': [10, 25, 50, 100]
            }"
            class="clients-table striped-table"
            dense
          >
            <!-- Client Name Column -->
            <template v-slot:item.clientName="{ item }">
              <div class="d-flex align-center">
                <font-awesome-icon
                  v-if="hasIssues(item)"
                  :icon="['fas', 'exclamation-triangle']"
                  class="error--text mr-2"
                  size="sm"
                />
                <span class="font-weight-medium">{{ item.clientName }}</span>
              </div>
            </template>

            <!-- Billing Code Column -->
            <template v-slot:item.billingCode="{ item }">
              <v-chip
                small
                outlined
                color="primary"
              >
                {{ item.billingCode }}
              </v-chip>
            </template>

            <!-- Status Column -->
            <template v-slot:item.isActive="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <font-awesome-icon
                    :icon="['fas', item.isActive ? 'circle-check' : 'circle-xmark']"
                    :style="{ color: getClientStatusColor(item) }"
                    size="lg"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span>{{ item.isActive ? 'Active' : 'Inactive' }}</span>
              </v-tooltip>
            </template>

            <!-- Timesheet Column -->
            <template v-slot:item.isTimesheetEnabled="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <font-awesome-icon
                    :icon="['fas', item.isTimesheetEnabled ? 'circle-check' : 'circle-minus']"
                    :style="{ color: item.isTimesheetEnabled ? '#4caf50' : '#bdbdbd' }"
                    size="lg"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span>{{ item.isTimesheetEnabled ? 'Timesheet Enabled' : 'Timesheet Disabled' }}</span>
              </v-tooltip>
            </template>

            <!-- Config Column -->
            <template v-slot:item.config="{ item }">
              <v-skeleton-loader
                v-if="isLoadingConfig(item.billingCode)"
                type="text"
                max-width="80"
              />
              <font-awesome-icon
                v-else
                :icon="['fas', configs[item.billingCode] ? 'circle-check' : 'circle-xmark']"
                :style="{ color: configs[item.billingCode] ? '#4caf50' : '#f44336' }"
                size="lg"
              />
            </template>

            <!-- Publisher Column -->
            <template v-slot:item.publisher="{ item }">
              <v-skeleton-loader
                v-if="isLoadingPublisher(item.billingCode)"
                type="text"
                max-width="80"
              />
              <font-awesome-icon
                v-else
                :icon="['fas', publishers[item.billingCode] ? 'circle-check' : 'circle-xmark']"
                :style="{ color: publishers[item.billingCode] ? '#4caf50' : '#f44336' }"
                size="lg"
              />
            </template>

            <!-- Actions Column -->
            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                small
                color="primary"
                @click="navigateToProfile(item)"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <font-awesome-icon
                      :icon="['fas', 'eye']"
                      size="sm"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <span>View Profile</span>
                </v-tooltip>
              </v-btn>

              <v-btn
                v-if="isUserInternalAdmin"
                icon
                small
                color="primary"
                @click="editClient(item)"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <font-awesome-icon
                      :icon="['fas', 'edit']"
                      size="sm"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <span>Edit</span>
                </v-tooltip>
              </v-btn>

              <v-btn
                v-if="isUserInternalAdmin"
                icon
                small
                color="error"
                @click="deleteClient(item)"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <font-awesome-icon
                      :icon="['fas', 'trash']"
                      size="sm"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Organization Modal (for Create/Edit/Delete) -->
    <org-modal @data-updated="fetchClientsData" />
  </v-col>
</template>

<script>
/**
 * Organization Component - Refactored Client Management View
 *
 * This component displays clients in a card-based tabbed layout.
 * Follows the Vue 3 migration pattern with component-level state management.
 *
 * Key Features:
 * - Tabbed interface (Active, All, Inactive, Issues, Timesheet-Enabled)
 * - Card-based layout with loading skeletons
 * - Component-level data management
 * - Direct API calls (no Vuex dependencies for data fetching)
 * - Client-side filtering and search
 * - Tab-specific summaries
 * - Progressive data loading (clients → configs → publishers)
 */

import { createNamespacedHelpers } from 'vuex'

import api from '@/store/api.js'
import { firecollection } from '@/store/firebase.js'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'ClientManagement',
  components: {
    ClientCard: () => import('../components/ClientCard'),
    OrgModal: () => import('../components/OrgModal'),
  },
  data() {
    return {
      // Tab configuration
      activeTab: 0,
      tabs: [
        {
          id: 'active',
          label: 'Active Clients',
          icon: ['fas', 'check-circle'],
        },
        {
          id: 'all',
          label: 'All Clients',
          icon: ['fas', 'building'],
        },
        {
          id: 'inactive',
          label: 'Inactive',
          icon: ['fas', 'ban'],
        },
        {
          id: 'issues',
          label: 'Issues',
          icon: ['fas', 'exclamation-triangle'],
        },
        {
          id: 'timesheet',
          label: 'Timesheet Enabled',
          icon: ['fas', 'clock'],
        },
      ],

      // Component-level state
      clients: [],
      publishers: {},
      configs: {},
      services: [],
      loading: false,
      loadingConfigs: false,
      loadingPublishers: false,
      error: null,

      // View mode (persisted to localStorage)
      viewMode: 'grid', // 'grid' or 'table'

      // Search and filter state
      searchText: '',
      filterMenuOpen: false,
      advancedFiltersOpen: null, // Expansion panel state
      selectedStatusFilter: null,
      selectedTimesheetFilter: null,
      selectedConfigFilter: null,
      selectedPublisherFilter: null,
    }
  },
  computed: {
    ...mapAuthGetters(['isUserInternalAdmin']),
    ...mapToolsState({
      selectedSearch: (state) => state.selectedSearch,
    }),

    reportId() {
      return this.selectedSearch && this.selectedSearch.reportId
    },

    currentTab() {
      return this.tabs[this.activeTab] || this.tabs[0]
    },

    /**
     * Get clients for the current tab
     */
    tabClients() {
      const { id } = this.currentTab

      switch (id) {
        case 'active':
          return this.clients.filter((client) => {
            const config = this.configs[client.billingCode] || {}
            const isClientActive = client.isActive
            const isConfigActive = config.access?.active !== false
            return isClientActive && isConfigActive
          })

        case 'inactive':
          return this.clients.filter((client) => {
            const config = this.configs[client.billingCode] || {}
            const isClientActive = client.isActive
            const isConfigActive = config.access?.active !== false
            return !isClientActive || !isConfigActive
          })

        case 'issues':
          return this.clients.filter((client) => {
            const config = this.configs[client.billingCode]
            const publisher = this.publishers[client.billingCode]
            const hasNoConfig = !config
            const hasNoPublisher = !publisher
            const hasMismatch = client.clientId && publisher?.publisherId
              && client.clientId !== publisher.publisherId
            return hasNoConfig || hasNoPublisher || hasMismatch
          })

        case 'timesheet':
          return this.clients.filter((client) => client.isTimesheetEnabled)

        case 'all':
        default:
          return this.clients
      }
    },

    /**
     * Apply search and filters to tab clients
     */
    filteredClients() {
      let filtered = this.tabClients

      // Apply search
      if (this.searchText && typeof this.searchText === 'string') {
        const searchTerm = this.searchText.toLowerCase()
        filtered = filtered.filter((client) => {
          const nameMatch = client.clientName
            && typeof client.clientName === 'string'
            && client.clientName.toLowerCase().includes(searchTerm)
          const codeMatch = client.billingCode
            && typeof client.billingCode === 'string'
            && client.billingCode.toLowerCase().includes(searchTerm)
          return nameMatch || codeMatch
        })
      }

      // Apply status filter
      if (this.selectedStatusFilter !== null) {
        filtered = filtered.filter((client) => {
          const config = this.configs[client.billingCode] || {}
          const isActive = client.isActive && config.access?.active !== false
          return this.selectedStatusFilter === 'active' ? isActive : !isActive
        })
      }

      // Apply timesheet filter
      if (this.selectedTimesheetFilter !== null) {
        filtered = filtered.filter((client) => {
          const isEnabled = client.isTimesheetEnabled
          return this.selectedTimesheetFilter === 'enabled' ? isEnabled : !isEnabled
        })
      }

      // Apply config filter
      if (this.selectedConfigFilter !== null) {
        filtered = filtered.filter((client) => {
          const hasConfig = !!this.configs[client.billingCode]
          return this.selectedConfigFilter === 'has-config' ? hasConfig : !hasConfig
        })
      }

      // Apply publisher filter
      if (this.selectedPublisherFilter !== null) {
        filtered = filtered.filter((client) => {
          const hasPublisher = !!this.publishers[client.billingCode]
          return this.selectedPublisherFilter === 'has-publisher' ? hasPublisher : !hasPublisher
        })
      }

      return filtered
    },

    /**
     * Filter options
     */
    statusFilterOptions() {
      return [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ]
    },

    timesheetFilterOptions() {
      return [
        { text: 'Enabled', value: 'enabled' },
        { text: 'Disabled', value: 'disabled' },
      ]
    },

    configFilterOptions() {
      return [
        { text: 'Has Configuration', value: 'has-config' },
        { text: 'Missing Configuration', value: 'missing-config' },
      ]
    },

    publisherFilterOptions() {
      return [
        { text: 'Has Publisher', value: 'has-publisher' },
        { text: 'Missing Publisher', value: 'missing-publisher' },
      ]
    },

    hasActiveFilters() {
      return !!(
        this.selectedStatusFilter
        || this.selectedTimesheetFilter
        || this.selectedConfigFilter
        || this.selectedPublisherFilter
      )
    },

    activeFilterCount() {
      let count = 0
      if (this.selectedStatusFilter) count += 1
      if (this.selectedTimesheetFilter) count += 1
      if (this.selectedConfigFilter) count += 1
      if (this.selectedPublisherFilter) count += 1
      return count
    },

    /**
     * Table headers for table view
     */
    tableHeaders() {
      return [
        {
          text: 'Client Name',
          value: 'clientName',
          sortable: true,
          align: 'start',
        },
        {
          text: 'Billing Code',
          value: 'billingCode',
          sortable: true,
          align: 'start',
        },
        {
          text: 'Status',
          value: 'isActive',
          sortable: true,
          align: 'center',
        },
        {
          text: 'Timesheet',
          value: 'isTimesheetEnabled',
          sortable: true,
          align: 'center',
        },
        {
          text: 'Config',
          value: 'config',
          sortable: false,
          align: 'center',
        },
        {
          text: 'Publisher',
          value: 'publisher',
          sortable: false,
          align: 'center',
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '150px',
        },
      ]
    },
  },

  watch: {
    activeTab() {
      // Clear tab-specific filters when switching tabs
      this.clearAllFilters()
    },

    viewMode(newMode) {
      // Persist view mode to localStorage
      localStorage.setItem('clientsViewMode', newMode)
    },

    clients() {
      this.updateTabCounts()
    },

    configs: {
      handler() {
        this.updateTabCounts()
      },
      deep: true,
    },

    publishers: {
      handler() {
        this.updateTabCounts()
      },
      deep: true,
    },
  },

  async mounted() {
    // Restore view mode from localStorage
    const savedViewMode = localStorage.getItem('clientsViewMode')
    if (savedViewMode && ['grid', 'table'].includes(savedViewMode)) {
      this.viewMode = savedViewMode
    }

    await this.fetchClientsData()
  },

  methods: {
    /**
     * Check if config is loading for a specific client
     */
    isLoadingConfig(billingCode) {
      return this.loadingConfigs && !this.configs[billingCode]
    },

    /**
     * Check if publisher is loading for a specific client
     */
    isLoadingPublisher(billingCode) {
      return this.loadingPublishers && !this.publishers[billingCode]
    },

    /**
     * Update tab counts based on current data
     */
    updateTabCounts() {
      // Safety check to ensure tabs exist
      if (!this.tabs || !Array.isArray(this.tabs)) {
        return
      }

      this.tabs = this.tabs.map((tab) => {
        let count = 0
        let chipColor = 'brightblue' // Default color (primary)

        switch (tab.id) {
          case 'active':
            count = this.clients.filter((client) => {
              const config = this.configs[client.billingCode] || {}
              return client.isActive && config.access?.active !== false
            }).length
            chipColor = 'grassgreen'
            break

          case 'all':
            count = this.clients.length
            chipColor = 'brightblue'
            break

          case 'inactive':
            count = this.clients.filter((client) => {
              const config = this.configs[client.billingCode] || {}
              return !client.isActive || config.access?.active === false
            }).length
            chipColor = 'gray'
            break

          case 'issues':
            count = this.clients.filter((client) => {
              const config = this.configs[client.billingCode]
              const publisher = this.publishers[client.billingCode]
              const hasNoConfig = !config
              const hasNoPublisher = !publisher
              const hasMismatch = client.clientId && publisher?.publisherId
                && client.clientId !== publisher.publisherId
              return hasNoConfig || hasNoPublisher || hasMismatch
            }).length
            chipColor = 'toasted'
            break

          case 'timesheet':
            count = this.clients.filter((c) => c.isTimesheetEnabled).length
            chipColor = 'brightblue'
            break

          default:
            count = 0
        }

        return { ...tab, count, chipColor }
      })
    },

    /**
     * Clear all active filters
     */
    clearAllFilters() {
      this.searchText = ''
      this.selectedStatusFilter = null
      this.selectedTimesheetFilter = null
      this.selectedConfigFilter = null
      this.selectedPublisherFilter = null
      this.filterMenuOpen = false
    },

    /**
     * Navigate to client profile
     * Uses the legacy pattern to ensure proper data loading
     */
    async navigateToProfile(client) {
      const { billingCode } = client

      // Push to the route first
      await this.$router.push(`/internal/organization/${billingCode}`)

      // Trigger the main content render to load the profile data
      await this.$store.dispatch('tools/renderMainContent', this.$route)
    },

    /**
     * Add new client (Admin only)
     */
    addClient() {
      this.$store.dispatch('internal/showFormOrganizationCreate')
    },

    /**
     * Edit client
     */
    editClient(client) {
      this.$store.dispatch('internal/showFormOrganizationUpdate', client)
    },

    /**
     * Delete client
     */
    deleteClient(client) {
      this.$store.dispatch('internal/showFormOrganizationDelete', client)
    },

    /**
     * Check if client has issues (for table view)
     */
    hasIssues(client) {
      const config = this.configs[client.billingCode]
      const publisher = this.publishers[client.billingCode]
      const hasNoConfig = !config
      const hasNoPublisher = !publisher
      const hasMismatch = client.clientId && publisher?.publisherId
        && client.clientId !== publisher.publisherId
      return hasNoConfig || hasNoPublisher || hasMismatch
    },

    /**
     * Get client status color
     */
    getClientStatusColor(client) {
      const config = this.configs[client.billingCode] || {}
      const isConfigActive = config.access?.active !== false
      const isClientActive = client.isActive

      if (!isClientActive || !isConfigActive) {
        return '#ff9800' // warning (Material Design orange)
      }

      return '#4caf50' // success (Material Design green)
    },

    /**
     * Get organization configs from Firestore
     */
    async getOrgConfigs(catcher) {
      try {
        const configs = await firecollection.collection('organization', catcher)
        return configs || []
      } catch (error) {
        catcher(error)
        return []
      }
    },

    /**
     * Fetch all client data
     * Phase 1: Load clients immediately
     * Phase 2: Load configs from Firestore
     * Phase 3: Load publishers from API
     * Phase 4: Merge data
     */
    async fetchClientsData() {
      this.loading = true
      this.error = null

      const catcher = (error) => {
        this.error = error.message || 'Failed to load clients'

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Error loading clients: ${this.error}`,
          status: 'error',
        }, { root: true })

        // eslint-disable-next-line no-console
        console.error('Error fetching clients:', error)
      }

      try {
        // Phase 1: Load clients immediately
        const clientsResponse = await api.getClients()
        const clients = clientsResponse?.content || []

        // Transform clients to add derived properties (matching organization.js mapClientAndPublisher logic)
        const transformedClients = clients.map((client) => {
          // Set isTimesheetEnabled based on organization.js line 152 logic
          const isTimesheetEnabled = !!client.billingCode && !client.isDeleted
          const hasTimesheetReport = isTimesheetEnabled

          return {
            ...client,
            isTimesheetEnabled,
            hasTimesheetReport,
          }
        })

        this.clients = transformedClients
        this.loading = false

        // Show initial success message
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Loaded ${clients.length} clients`,
          status: 'success',
        }, { root: true })

        // Phase 2: Load configs in background
        this.loadingConfigs = true
        const configsArray = await this.getOrgConfigs(catcher)

        // Convert configs array to lookup object by billingCode (docId)
        const configsMap = {}
        configsArray.forEach((config) => {
          if (config.docId) {
            configsMap[config.docId] = config
          }
        })
        this.configs = configsMap
        this.loadingConfigs = false

        // Phase 3: Load publishers in background
        this.loadingPublishers = true
        const publishersResponse = await api.getPublishers()
        const publishers = publishersResponse?.content || []

        // Convert publishers array to lookup object by billingCode
        const publishersMap = {}
        publishers.forEach((publisher) => {
          if (publisher.billingCode) {
            publishersMap[publisher.billingCode] = publisher
          }
        })
        this.publishers = publishersMap
        this.loadingPublishers = false

        // Phase 4: Load services
        const servicesResponse = await api.getServices()
        this.services = servicesResponse?.content || []

        // Update tab counts after all data is loaded
        this.updateTabCounts()
      } catch (error) {
        catcher(error)
      } finally {
        this.loading = false
        this.loadingConfigs = false
        this.loadingPublishers = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Header styles (matching Time view pattern)
.clients-view-header-compact {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden; // Ensures children respect border-radius

  .header-toolbar {
    min-height: 56px !important;
    max-height: 56px !important;
    background: white !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  .header-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(var(--v-primary-base), 0.08);
  }

  .clients-view-tabs-inline {
    ::v-deep .v-tabs-bar {
      height: 56px !important;
      background-color: white !important;
    }

    ::v-deep .v-tab {
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: 0.25px;
      text-transform: none;
      min-width: 120px;
      padding: 0 16px;
      height: 56px;

      &:hover {
        background-color: rgba(var(--v-primary-base), 0.04);
      }
    }

    ::v-deep .v-tab--active {
      color: var(--v-primary-base);
    }

    ::v-deep .v-tabs-slider {
      height: 3px !important;
    }
  }

  .tab-compact {
    padding: 0 16px;
  }

  .add-client-btn-compact {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
  }

  // Tab count chips styling
  .tab-count-chip {
    background-color: #e0e0e0 !important;
    border-color: #bdbdbd !important;
    color: #616161 !important;
    font-weight: 600;
  }
}

// Controls bar styles (matching Time view)
.controls-bar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);

  .search-field-compact {
    ::v-deep .v-input__control {
      min-height: 40px;
    }

    ::v-deep .v-input__slot {
      background-color: white !important;
    }

    .search-icon {
      color: rgba(0, 0, 0, 0.54);
    }
  }

  .filter-btn-compact {
    text-transform: none;
    letter-spacing: normal;
    height: 40px;

    &.filter-active {
      background-color: rgba(var(--v-primary-base), 0.08) !important;
      border-color: var(--v-primary-base) !important;
      border-width: 2px !important;
    }
  }
}

// Filter dialog styles
.filter-section {
  .v-expansion-panel {
    background: transparent !important;

    ::v-deep .v-expansion-panel-header {
      min-height: 48px;
      padding: 12px 0;
    }

    ::v-deep .v-expansion-panel-content__wrap {
      padding: 12px 0;
    }
  }
}

// Table view styles
.clients-table {
  &.striped-table {
    ::v-deep tbody tr:nth-of-type(odd) {
      background-color: rgba(0, 0, 0, 0.02);
    }

    ::v-deep tbody tr:hover {
      background-color: rgba(var(--v-primary-base), 0.04) !important;
    }
  }

  ::v-deep .v-data-table-header th {
    font-weight: 600 !important;
    font-size: 0.875rem !important;
    color: rgba(0, 0, 0, 0.87) !important;
  }

  ::v-deep td {
    font-size: 0.875rem;
  }
}

// Dark mode support
.theme--dark {
  .clients-view-header-compact {
    background: #2d2d2d;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);

    .header-toolbar {
      background: #2d2d2d !important;
      border-bottom-color: rgba(255, 255, 255, 0.12);
    }

    .header-icon-wrapper {
      background: rgba(var(--v-primary-base), 0.15);
    }

    .clients-view-tabs-inline {
      ::v-deep .v-tabs-bar {
        background-color: #2d2d2d !important;
      }
    }

    .tab-count-chip {
      background-color: #424242 !important;
      border-color: #616161 !important;
      color: #e0e0e0 !important;
    }
  }

  .controls-bar {
    background: rgba(255, 255, 255, 0.05);
    border-bottom-color: rgba(255, 255, 255, 0.12);

    .search-field-compact {
      ::v-deep .v-input__slot {
        background-color: rgba(255, 255, 255, 0.05) !important;
      }

      .search-icon {
        color: rgba(255, 255, 255, 0.7);
      }
    }

    .filter-btn-compact.filter-active {
      background-color: rgba(var(--v-primary-base), 0.15) !important;
    }
  }

  .clients-table {
    &.striped-table {
      ::v-deep tbody tr:nth-of-type(odd) {
        background-color: rgba(255, 255, 255, 0.02);
      }

      ::v-deep tbody tr:hover {
        background-color: rgba(var(--v-primary-base), 0.08) !important;
      }
    }

    ::v-deep .v-data-table-header th {
      color: rgba(255, 255, 255, 0.87) !important;
    }
  }
}
</style>
